import { useEffect, useRef, useState } from 'react'
import { InfoIcon } from 'flyt/ui/Icons/Icons'
import { Popover } from 'flyt/ui/Popover/Popover'
import type { InfoTipProps } from './InfoTip.types'

/**
 * info-tip — the quiet explanation that sits beside a field label.
 *
 * A form asks short questions, and a short question is often the wrong place
 * for the answer: "Sensitivitet" is one word, and what the reader needs is a
 * sentence with an example in it. So the sentence is not IN the form — it is
 * one small mark away, and the form stays a column of questions.
 *
 * THREE WAYS IN, BECAUSE THERE ARE THREE KINDS OF READER. The pointer opens it
 * by resting on the mark, the keyboard opens it by tabbing to it, and a finger
 * opens it by tapping. Hover alone would leave a touch reader with a control
 * that does nothing, which is worse than no control at all.
 *
 * THE PANEL DOES NOT TAKE FOCUS. It opens under a resting pointer, and moving
 * focus off the thing the pointer is resting on would close the panel in the
 * same frame it appeared. The reader is never moved anywhere they did not ask
 * to go, so tabbing on from the mark simply continues down the form.
 *
 * A short grace on the way out is what lets the pointer cross the gap between
 * the mark and the panel to read a long explanation; leaving both closes it.
 * Escape and a click outside close it too — the popover's own rules.
 */

/** How long the panel survives the pointer leaving, so the gap can be crossed. */
const CLOSE_GRACE_MS = 120

export function InfoTip({ label, children, scrollable = false }: InfoTipProps) {
  const [open, setOpen] = useState(false)
  const closing = useRef<number | undefined>(undefined)
  const trigger = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)

  // A pending close must not fire into an unmounted tip.
  useEffect(
    () => () => {
      window.clearTimeout(closing.current)
    },
    [],
  )

  const hold = (): void => {
    window.clearTimeout(closing.current)
    setOpen(true)
  }
  const release = (): void => {
    window.clearTimeout(closing.current)
    closing.current = window.setTimeout(() => {
      if (panel.current?.contains(document.activeElement)) return
      setOpen(false)
    }, CLOSE_GRACE_MS)
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      focusPanelOnOpen={false}
      restoreFocusOnClose={false}
      width="wide"
      trigger={
        // The mark: as quiet as a punctuation mark until it is wanted, and a
        // full-sized hit target around it. The glyph never answers the pointer,
        // so a press always lands on the button rather than on a path inside it.
        <button
          ref={trigger}
          type="button"
          aria-label={label}
          onPointerEnter={hold}
          onPointerLeave={release}
          onFocus={hold}
          onBlur={release}
          onKeyDown={(event) => {
            if (scrollable && open && (event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey))) {
              event.preventDefault()
              panel.current?.focus()
            }
          }}
          onClick={(event) => {
            // Hover or focus may already have opened the panel. Activation
            // must not let the popover toggle it closed again.
            event.preventDefault()
            hold()
          }}
          className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-text-muted outline-offset-2 hover:text-accent focus-visible:outline-2 focus-visible:outline-current [&_svg]:pointer-events-none"
        >
          <InfoIcon width={15} height={15} />
        </button>
      }
    >
      <div
        ref={panel}
        onPointerEnter={hold}
        onPointerLeave={release}
        {...(scrollable ? { tabIndex: 0, role: 'region', 'aria-label': label, onFocus: hold, onBlur: release } : {})}
        onKeyDown={(event) => {
          if (!scrollable || (event.key !== 'Tab' && event.key !== 'Escape')) return
          // Return to the trigger before native Tab advances to the next form
          // field. Shift+Tab and Escape return to the trigger itself.
          if (event.shiftKey || event.key === 'Escape') event.preventDefault()
          trigger.current?.focus()
          setOpen(false)
        }}
        className={`text-sm leading-relaxed text-text ${scrollable ? 'max-h-[min(70dvh,calc(var(--radix-popover-content-available-height)-1.5rem))] overflow-y-auto overscroll-contain pr-2 outline-offset-2 focus-visible:outline-2' : ''}`}
      >
        {children}
      </div>
    </Popover>
  )
}
